import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { memo, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '~/assets/icons';
import { IAlarmContact, IAlertContact, IAlertDetail, ISensorInfo, IUserInfo } from '~/utils/interface/alert';
import alertApi from '~/apis/alert';
import { handleApiError } from '~/utils/helper';

export interface IAlertDetailResponse {
  alert: IAlertDetail;
  sensor_info: ISensorInfo;
  user_info: IUserInfo;
  contact?: IAlertContact;
}

type AlertDetailDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  data?: { id: string } | any;
};

const DetailAlertDialog = (props: AlertDetailDialogProps) => {
  const { t } = useTranslation('admin/alert');

  const [detailData, setDetailData] = useState<IAlertDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const alert = detailData?.alert;
  const sensor_info = detailData?.sensor_info;
  const contacts = detailData?.contact;

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const fetchAlertDetail = useCallback(async (id: string) => {
    if (!id) return;
    setIsLoading(true);
    setDetailData(null);
    try {
      const response = await alertApi.getAlertDetail(id);
      setDetailData(response.data);
    } catch (error) {
      console.error('Failed to fetch alert detail:', error);
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (props.open && props.data?.id) {
      fetchAlertDetail(props.data.id);
    }
    if (!props.open) {
      setDetailData(null);
    }
  }, [props.open, props.data?.id, fetchAlertDetail]);

  const AlertInfoRow = ({
    label,
    value,
    color,
    isBold,
  }: {
    label: string;
    value: string | number | undefined | null;
    color?: string;
    isBold?: boolean;
  }) => (
    <Typography variant='body2' className='text-[13px]' style={{ lineHeight: 1.7, marginBottom: '4px' }}>
      <span className='font-bold inline-block'>{label}:</span>
      <span className={`pl-2 ${isBold ? 'font-bold' : ''}`} style={color ? { color } : undefined}>
        {value ?? ''}
      </span>
    </Typography>
  );

  const getSensorTypeLabel = (code: number | undefined): string => {
    if (code === undefined) return '';
    const value = Number(code);
    switch (value) {
      case 17:
        return t('sensorType.smoke');
      case 18:
        return t('sensorType.thermal');
      case 19:
        return t('sensorType.device');
      case 21:
        return t('sensorType.button');
      default:
        return String(code);
    }
  };
  const getManagerRole = (code: string | null): string => {
    if (!code) return '';
    switch (code.toLowerCase()) {
      case 'lv1':
        return t('manager_role_options.lv1');
      case 'lv2':
        return t('manager_role_options.lv2');
      case 'lv3':
        return t('manager_role_options.lv3');
      default:
        return String(code);
    }
  };
  const getOverallStatusLabel = (status: number | undefined): { text: string; color: string } | null => {
    if (status === undefined || status === null) return null;
    if (status === 0) {
      return { text: t('feedBack.unDone'), color: '#ED1002' };
    }
    if (status === 1 || status === 2 || status === 3) {
      return { text: t('feedBack.done'), color: '#3D9149' };
    }
    return { text: String(status), color: '#3A3F51' };
  };

  if (isLoading || !detailData) {
    return (
      <Dialog
        open={props.open}
        onClose={() => handleSetOpen(false)}
        sx={{
          '& .MuiDialog-paper': {
            width: '888px',
            minWidth: '888px',
            minHeight: '464px',
            borderRadius: '20px',
          },
        }}
      >
        <DialogTitle className=' bg-[#ED1002] '>
          <Typography className='font-bold text-white'>{t('alertDetail.title')}</Typography>
          <IconButton onClick={() => handleSetOpen(false)} className='text-white'>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={props.open}
      onClose={() => handleSetOpen(false)}
      sx={{
        '& .MuiDialog-paper': {
          width: '888px',
          minWidth: '888px',
          minHeight: alert?.status !== 0 ? '464px' : '400px',
          borderRadius: '20px',
        },
      }}
    >
      <DialogTitle className=' bg-[#ED1002] '>
        <Typography variant='h6' className='font-bold text-white '>
          {t('alertDetail.title')}
        </Typography>
        <IconButton onClick={() => handleSetOpen(false)} className='text-white'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          overflowY: 'auto',
          paddingLeft: '25px !important',
          paddingRight: '25px !important',
        }}
      >
        <Box className='flex flex-grow'>
          <Box className='w-[55%] pr-3 '>
            <Typography className='font-bold text-[16px] mb-2' color='#ED1002'>
              {t('alertDetail.infoTitle')}
            </Typography>
            <Box>
              <AlertInfoRow
                label={t('alertDetail.type')}
                value={
                  alert?.alert_subject === 0
                    ? t('alert_subject_options.unknow')
                    : alert?.alert_subject === 1
                    ? t('alert_subject_options.warning')
                    : alert?.alert_subject === 2
                    ? t('alert_subject_options.facility')
                    : alert?.alert_subject === 3
                    ? t('alert_subject_options.device')
                    : alert?.alert_subject === 4
                    ? t('alert_subject_options.sensor')
                    : alert?.alert_subject === 5
                    ? t('alert_subject_options.agent')
                    : alert?.alert_subject === 6
                    ? t('alert_subject_options.distributor')
                    : alert?.alert_subject === 7
                    ? t('alert_subject_options.serviceRenewal')
                    : alert?.alert_subject === 8
                    ? t('alert_subject_options.firmwareVersion')
                    : alert?.alert_subject === 9
                    ? t('alert_subject_options.supportSession')
                    : alert?.alert_subject === 10
                    ? t('alert_subject_options.feedback')
                    : alert?.alert_subject
                }
              />
              <AlertInfoRow label={t('alertDetail.location')} value={sensor_info?.location ?? ''} />
              <AlertInfoRow label={t('alertDetail.address')} value={sensor_info?.location_address ?? ''} />
              <AlertInfoRow label={t('alertDetail.deviceCode')} value={sensor_info?.device_code ?? ''} />
              <AlertInfoRow label={t('alertDetail.sensorCode')} value={sensor_info?.sensor_code ?? ''} />
              <AlertInfoRow label={t('alertDetail.locationAlert')} value={sensor_info?.sensor_location ?? ''} />
              <AlertInfoRow
                label={t('alertDetail.sensorType')}
                value={getSensorTypeLabel(sensor_info?.sensor_type) ?? ''}
              />
              <AlertInfoRow label={t('alertDetail.time')} value={dayjs(alert?.created_at).format('HH:mm DD/MM/YYYY')} />
              <AlertInfoRow
                label={t('alertDetail.status')}
                value={getOverallStatusLabel(alert?.status)?.text ?? ''}
                color={getOverallStatusLabel(alert?.status)?.color}
                isBold
              />
            </Box>

            <Typography className='font-bold text-[16px] mb-1 mt-3' color='#ED1002'>
              {t('alertDetail.feedbackTitle')}
            </Typography>
            <Box>
              {alert?.status === 0 ? (
                <Typography className='font-bold mb-1 text-[13px] mt-2' style={{ color: '#ED1002' }}>
                  {t('feedBack.unDone')}
                </Typography>
              ) : (
                <Typography className='font-bold mb-1 text-[13px] mt-2' style={{ color: '#3D9149' }}>
                  {t('feedBack.done')}
                </Typography>
              )}

              {alert?.status === 1 ? (
                <Typography className='mb-1 text-[13px] mt-2'>{t('feedBack.realFire')}</Typography>
              ) : alert?.status === 2 ? (
                <Typography className='mb-1 text-[13px] mt-2'>{t('feedBack.fakeFire')}</Typography>
              ) : alert?.status === 3 ? (
                <Typography className='mb-1 text-[13px] mt-2'>{t('feedBack.done')}</Typography>
              ) : null}

              <AlertInfoRow
                label={t('alertDetail.confirmedAt')}
                value={alert?.confirmed_at ? dayjs(alert?.confirmed_at).format('HH:mm DD/MM/YYYY') : ''}
              />
            </Box>
          </Box>

          <Box className='w-[45%] pl-3'>
            <Typography className='font-bold text-[16px] mb-2' color='#ED1002'>
              {t('alertDetail.contactTitle')}
            </Typography>
            {contacts?.alarm_contacts?.length ? (
              <Table
                size='small'
                sx={{ borderCollapse: 'collapse', '& td, & th': { border: '1px solid #ED1002', padding: '5px 8px' } }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      border: '2px solid #ED1002',
                      borderBottom: 'none',
                      '& th': {
                        backgroundColor: '#ED1002',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '13px',
                        borderTop: 'none',
                        borderBottom: 'none',
                        borderRight: '1px solid #e9dfde',
                        '&:first-of-type': {
                          borderLeft: 'none',
                        },
                        '&:last-child': {
                          borderRight: 'none',
                        },
                      },
                    }}
                  >
                    <TableCell style={{ width: '35%' }} align='center'>
                      {t('alertDetail.contactName')}
                    </TableCell>
                    <TableCell style={{ width: '30%' }} align='center'>
                      {t('alertDetail.contactPriority')}
                    </TableCell>
                    <TableCell style={{ width: '35%' }} align='center'>
                      {t('alertDetail.contactPhone')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contacts?.alarm_contacts?.map((contact: IAlarmContact, index: number) => (
                    <TableRow
                      key={index}
                      sx={{
                        '& td': {
                          backgroundColor: '#F9F9F9',
                          borderBottom: '1px solid #ED1002',
                          borderTop: 'none',
                          fontSize: '13px',
                        },
                      }}
                    >
                      <TableCell
                        align='center'
                        sx={{
                          borderRight: '1px solid #ED1002',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {contact.full_name}
                      </TableCell>
                      <TableCell align='center' sx={{ borderRight: '1px solid #ED1002' }}>
                        {getManagerRole(contact.manager_role)}
                      </TableCell>
                      <TableCell align='center'>{contact.phone_number}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography className='text-[13px] italic'>{t('noData')}</Typography>
            )}

            <Box>
              <Typography className='font-bold text-[16px] mb-2 my-7' color='#ED1002'>
                {t('alertDetail.noteTitle')}
              </Typography>
              <Box
                className='p-2 rounded text-[13px] whitespace-pre-wrap'
                style={{ border: '1px solid #d3d0cf', minHeight: '80px' }}
              >
                <Typography variant='body2'>{alert?.note}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default memo(DetailAlertDialog);
